import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { Database, RefreshCw, Search, Package, Clock, AlertTriangle, CheckCircle, Loader2, Trash2, Key } from 'lucide-react';

const SUPPLIER_COLORS = {
  Mobilax: { bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/40', dot: 'bg-orange-400' },
  JensMobiles: { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/40', dot: 'bg-blue-400' },
  'LCD-Phone': { bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/40', dot: 'bg-green-400' },
};

// Mapping backend (valeurs FR renvoyées par l'API) -> clés i18n pour l'affichage, repli sur valeur brute.
const STOCK_LABEL_KEYS = {
  'En stock': 'adminCache.inStock',
  'Rupture': 'adminCache.outOfStock',
  'En rupture': 'adminCache.outOfStock',
  'Rupture de stock': 'adminCache.outOfStock',
  'Épuisé': 'adminCache.outOfStock',
  'Indisponible': 'adminCache.outOfStock',
  'Hors stock': 'adminCache.outOfStock',
};

const StatusBadge = ({ status }) => {
  const { t } = useLanguage();
  const configs = {
    completed: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/15', label: t('adminCache.statusCompleted') },
    running: { icon: Loader2, color: 'text-cyan-400', bg: 'bg-cyan-500/15', label: t('adminCache.statusRunning') },
    updating: { icon: RefreshCw, color: 'text-yellow-400', bg: 'bg-yellow-500/15', label: t('adminCache.statusUpdating') },
    error: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/15', label: t('adminCache.statusError') },
  };
  const cfg = configs[status] || configs.error;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color}`}>
      <Icon className={`w-3 h-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
};

const AdminCachePage = () => {
  const { api } = useAuth();
  const { t, formatCurrency, formatDate, formatNumber } = useLanguage();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCatalog, setSearchCatalog] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await api.get('/supplier-parts/status');
      setStatus(resp.data);
    } catch (err) {
      console.error('Failed to fetch status:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const triggerScrape = async (supplier = null) => {
    setScraping(supplier || 'all');
    try {
      const url = supplier ? `/supplier-parts/trigger-scrape?supplier=${supplier}` : '/supplier-parts/trigger-scrape';
      await api.post(url);
      setTimeout(fetchStatus, 2000);
    } catch (err) {
      console.error('Scrape trigger error:', err);
    } finally {
      setTimeout(() => setScraping(null), 3000);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) return;
    setSearching(true);
    try {
      const params = new URLSearchParams({ query: searchQuery, limit: '100' });
      if (searchCatalog) params.set('catalog_type', searchCatalog);
      const resp = await api.get(`/supplier-parts/search?${params.toString()}`);
      setSearchResults(resp.data.results || []);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  const formatLastRun = (iso) => {
    if (!iso) return t('adminCache.never');
    return formatDate(iso, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Database className="w-6 h-6 text-cyan-400" />
            <h1 className="text-2xl font-bold text-white">{t('adminCache.title')}</h1>
          </div>
          <button
            onClick={() => triggerScrape()}
            disabled={!!scraping}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors disabled:opacity-50"
            data-testid="trigger-full-scrape"
          >
            <RefreshCw className={`w-4 h-4 ${scraping ? 'animate-spin' : ''}`} />
            {scraping ? t('adminCache.refreshing') : t('adminCache.refreshData')}
          </button>
        </div>

        <div className="rounded-xl border border-amber-500/25 bg-amber-950/20 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <Key className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-100/90 leading-relaxed">
              <strong>{t('adminCache.step1Title')}</strong> {t('adminCache.step1Intro')}{' '}
              <Link to="/admin/identifiants-fournisseurs" className="text-cyan-400 hover:underline font-semibold">{t('adminCache.step1LinkText')}</Link>.{' '}
              {t('adminCache.step1Outro', { action: t('adminCache.refreshData') })}
            </div>
          </div>
          <Link
            to="/admin/identifiants-fournisseurs"
            className="shrink-0 inline-flex items-center justify-center px-4 py-2 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 text-sm font-semibold"
          >
            {t('adminCache.configurePasswords')}
          </Link>
        </div>

        {/* Stats cards */}
        {!loading && status && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="text-3xl font-bold text-white">{formatNumber(status.total_parts || 0)}</div>
                <div className="text-sm text-gray-400 mt-1">{t('adminCache.totalCached')}</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="text-2xl font-bold text-emerald-400">{formatNumber(status.parts_by_catalog?.part || 0)}</div>
                <div className="text-sm text-gray-400 mt-1">{t('adminCache.spareParts')}</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="text-2xl font-bold text-violet-400">{formatNumber(status.parts_by_catalog?.accessory || 0)}</div>
                <div className="text-sm text-gray-400 mt-1">{t('adminCache.accessories')}</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="text-2xl font-bold text-amber-400">{formatNumber(status.parts_by_catalog?.phone || 0)}</div>
                <div className="text-sm text-gray-400 mt-1">{t('adminCache.phones')}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(status.parts_by_supplier || {}).map(([supplier, count]) => {
                const colors = SUPPLIER_COLORS[supplier] || {};
                return (
                  <div key={supplier} className={`${colors.bg} border ${colors.border} rounded-xl p-4`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`text-2xl font-bold ${colors.text}`}>{formatNumber(count || 0)}</div>
                        <div className="text-sm text-gray-400 mt-1">{supplier}</div>
                      </div>
                      <button
                        onClick={() => triggerScrape(supplier.toLowerCase().replace('-', ''))}
                        disabled={!!scraping}
                        className={`p-2 rounded-lg ${colors.bg} hover:bg-white/10 transition-colors`}
                        data-testid={`trigger-scrape-${supplier}`}
                      >
                        <RefreshCw className={`w-4 h-4 ${colors.text} ${scraping === supplier.toLowerCase() ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Scrape statuses */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-cyan-400" />
                {t('adminCache.syncState')}
              </h2>
              <div className="space-y-3">
                {(status.statuses || []).map((s) => (
                  <div key={s.supplier} className="flex items-center justify-between bg-white/5 rounded-lg px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${(SUPPLIER_COLORS[s.supplier] || SUPPLIER_COLORS.Mobilax).dot}`} />
                      <span className="text-white font-medium">{s.supplier}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <StatusBadge status={s.status} />
                      <span className="text-sm text-gray-400">{formatLastRun(s.last_run)}</span>
                      {s.error && (
                        <span className="text-xs text-red-400 max-w-xs truncate">{s.error}</span>
                      )}
                    </div>
                  </div>
                ))}
                {(!status.statuses || status.statuses.length === 0) && (
                  <p className="text-sm text-gray-500 text-center py-2">{t('adminCache.noSync')}</p>
                )}
              </div>
            </div>
          </>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        )}

        {/* Search/Browse */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <Search className="w-4 h-4 text-cyan-400" />
            {t('adminCache.exploreCache')}
          </h2>
          <div className="flex gap-2 mb-4 flex-wrap">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={t('adminCache.searchPlaceholder')}
              className="input-dark flex-1 min-w-[200px] py-2 px-3"
              data-testid="cache-search-input"
            />
            <select
              value={searchCatalog}
              onChange={(e) => setSearchCatalog(e.target.value)}
              className="input-dark py-2 px-3 min-w-[140px]"
              data-testid="cache-catalog-filter"
            >
              <option value="">{t('adminCache.allTypes')}</option>
              <option value="part">{t('adminCache.filterParts')}</option>
              <option value="accessory">{t('adminCache.filterAccessories')}</option>
              <option value="phone">{t('adminCache.filterPhones')}</option>
            </select>
            <button
              onClick={handleSearch}
              disabled={searching}
              className="px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors"
              data-testid="cache-search-button"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div className="overflow-auto max-h-[500px] rounded-lg border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-white/5 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 text-gray-400 font-medium">{t('adminCache.colType')}</th>
                    <th className="text-left px-3 py-2 text-gray-400 font-medium">{t('adminCache.colSupplier')}</th>
                    <th className="text-left px-3 py-2 text-gray-400 font-medium">{t('adminCache.colPart')}</th>
                    <th className="text-left px-3 py-2 text-gray-400 font-medium">{t('adminCache.colBrand')}</th>
                    <th className="text-right px-3 py-2 text-gray-400 font-medium">{t('adminCache.colPrice')}</th>
                    <th className="text-center px-3 py-2 text-gray-400 font-medium">{t('adminCache.colStock')}</th>
                    <th className="text-left px-3 py-2 text-gray-400 font-medium">{t('adminCache.colRef')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {searchResults.map((r, i) => {
                    const colors = SUPPLIER_COLORS[r.supplier] || {};
                    const catLabel = { part: t('adminCache.catPart'), accessory: t('adminCache.catAccessory'), phone: t('adminCache.catPhone') }[r.catalog_type] || t('adminCache.catPart');
                    const catColor = { part: 'text-emerald-400', accessory: 'text-violet-400', phone: 'text-amber-400' }[r.catalog_type] || 'text-gray-400';
                    const stockKey = STOCK_LABEL_KEYS[r.stock_status];
                    const stockLabel = stockKey ? t(stockKey) : (r.stock_status || '');
                    return (
                      <tr key={i} className="hover:bg-white/5 transition-colors">
                        <td className={`px-3 py-2 text-xs font-semibold ${catColor}`}>{catLabel}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${colors.bg} ${colors.text}`}>{r.supplier}</span>
                        </td>
                        <td className="px-3 py-2 text-gray-200 max-w-sm">
                          <a href={r.url} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors">
                            {r.part_name}
                          </a>
                        </td>
                        <td className="px-3 py-2 text-gray-400">{r.brand_name}</td>
                        <td className="px-3 py-2 text-right text-cyan-400 font-semibold">{formatCurrency(r.price)}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${r.stock_status === 'En stock' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                            {stockLabel}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{r.reference}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {searchResults.length === 0 && searchQuery && !searching && (
            <p className="text-sm text-gray-500 text-center py-4">{t('adminCache.noResults', { query: searchQuery })}</p>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminCachePage;
